import type { SupportedLang } from "@acme/validators";
import { StudioMode } from "@acme/validators";

import type { ExtendedStudioState } from "../use-studio-store";
import {
  angularDefaultCodemod,
  angularDefaultConfig,
  angularDefaultSource,
} from "../_data/default/angular";
import {
  cSharpDefaultCodemod,
  cSharpDefaultConfig,
  cSharpDefaultSource,
} from "../_data/default/c_sharp";
import {
  cppDefaultCodemod,
  cppDefaultConfig,
  cppDefaultSource,
} from "../_data/default/cpp";
import {
  cssDefaultCodemod,
  cssDefaultConfig,
  cssDefaultSource,
} from "../_data/default/css";
import {
  elixirDefaultCodemod,
  elixirDefaultConfig,
  elixirDefaultSource,
} from "../_data/default/elixir";
import {
  goDefaultCodemod,
  goDefaultConfig,
  goDefaultSource,
} from "../_data/default/go";
import {
  htmlDefaultCodemod,
  htmlDefaultConfig,
  htmlDefaultSource,
} from "../_data/default/html";
import {
  javaDefaultCodemod,
  javaDefaultConfig,
  javaDefaultSource,
} from "../_data/default/java";
import {
  javascriptDefaultCodemod,
  javascriptDefaultConfig,
  javascriptDefaultSource,
} from "../_data/default/javascript";
import {
  kotlinDefaultCodemod,
  kotlinDefaultConfig,
  kotlinDefaultSource,
} from "../_data/default/kotlin";
import {
  phpDefaultCodemod,
  phpDefaultConfig,
  phpDefaultSource,
} from "../_data/default/php";
import {
  pythonDefaultCodemod,
  pythonDefaultConfig,
  pythonDefaultSource,
} from "../_data/default/python";
import {
  rubyDefaultCodemod,
  rubyDefaultConfig,
  rubyDefaultSource,
} from "../_data/default/ruby";
import {
  rustDefaultCodemod,
  rustDefaultConfig,
  rustDefaultSource,
} from "../_data/default/rust";
import {
  scalaDefaultCodemod,
  scalaDefaultConfig,
  scalaDefaultSource,
} from "../_data/default/scala";
import {
  tsxDefaultCodemod,
  tsxDefaultConfig,
  tsxDefaultSource,
} from "../_data/default/tsx";
import {
  typescriptDefaultCodemod,
  typescriptDefaultConfig,
  typescriptDefaultSource,
} from "../_data/default/typescript";

export function getDefaultState(
  lang: SupportedLang,
): Pick<
  ExtendedStudioState,
  "lang" | "jsAstGrepCodemod" | "config" | "sources"
> {
  switch (lang) {
    case "tsx":
      return {
        lang: "tsx",
        jsAstGrepCodemod: tsxDefaultCodemod,
        config: tsxDefaultConfig,
        sources: [{ source: tsxDefaultSource, id: 1, autoGenerated: true }],
      };
    case "typescript":
      return {
        lang: "typescript",
        jsAstGrepCodemod: typescriptDefaultCodemod,
        config: typescriptDefaultConfig,
        sources: [
          { source: typescriptDefaultSource, id: 1, autoGenerated: true },
        ],
      };
    case "javascript":
      return {
        lang: "javascript",
        jsAstGrepCodemod: javascriptDefaultCodemod,
        config: javascriptDefaultConfig,
        sources: [
          { source: javascriptDefaultSource, id: 1, autoGenerated: true },
        ],
      };
    case "java":
      return {
        lang: "java",
        jsAstGrepCodemod: javaDefaultCodemod,
        config: javaDefaultConfig,
        sources: [{ source: javaDefaultSource, id: 1, autoGenerated: true }],
      };
    case "css":
      return {
        lang: "css",
        jsAstGrepCodemod: cssDefaultCodemod,
        config: cssDefaultConfig,
        sources: [{ source: cssDefaultSource, id: 1, autoGenerated: true }],
      };
    case "html":
      return {
        lang: "html",
        jsAstGrepCodemod: htmlDefaultCodemod,
        config: htmlDefaultConfig,
        sources: [{ source: htmlDefaultSource, id: 1, autoGenerated: true }],
      };
    case "kotlin":
      return {
        lang: "kotlin",
        jsAstGrepCodemod: kotlinDefaultCodemod,
        config: kotlinDefaultConfig,
        sources: [{ source: kotlinDefaultSource, id: 1, autoGenerated: true }],
      };
    case "c":
      return {
        lang: "c",
        jsAstGrepCodemod: scalaDefaultCodemod,
        config: scalaDefaultConfig,
        sources: [{ source: scalaDefaultSource, id: 1, autoGenerated: true }],
      };
    case "angular":
      return {
        lang: "angular",
        jsAstGrepCodemod: angularDefaultCodemod,
        config: angularDefaultConfig,
        sources: [{ source: angularDefaultSource, id: 1, autoGenerated: true }],
      };
    case "rust":
      return {
        lang: "rust",
        jsAstGrepCodemod: rustDefaultCodemod,
        config: rustDefaultConfig,
        sources: [{ source: rustDefaultSource, id: 1, autoGenerated: true }],
      };
    case "go":
      return {
        lang: "go",
        jsAstGrepCodemod: goDefaultCodemod,
        config: goDefaultConfig,
        sources: [{ source: goDefaultSource, id: 1, autoGenerated: true }],
      };
    case "ruby":
      return {
        lang: "ruby",
        jsAstGrepCodemod: rubyDefaultCodemod,
        config: rubyDefaultConfig,
        sources: [{ source: rubyDefaultSource, id: 1, autoGenerated: true }],
      };
    case "c_sharp":
      return {
        lang: "c_sharp",
        jsAstGrepCodemod: cSharpDefaultCodemod,
        config: cSharpDefaultConfig,
        sources: [{ source: cSharpDefaultSource, id: 1, autoGenerated: true }],
      };
    case "cpp":
      return {
        lang: "cpp",
        jsAstGrepCodemod: cppDefaultCodemod,
        config: cppDefaultConfig,
        sources: [{ source: cppDefaultSource, id: 1, autoGenerated: true }],
      };
    case "php":
      return {
        lang: "php",
        jsAstGrepCodemod: phpDefaultCodemod,
        config: phpDefaultConfig,
        sources: [{ source: phpDefaultSource, id: 1, autoGenerated: true }],
      };
    case "elixir":
      return {
        lang: "elixir",
        jsAstGrepCodemod: elixirDefaultCodemod,
        config: elixirDefaultConfig,
        sources: [{ source: elixirDefaultSource, id: 1, autoGenerated: true }],
      };
    case "python":
    default:
      return {
        lang,
        jsAstGrepCodemod: pythonDefaultCodemod,
        config: pythonDefaultConfig,
        sources: [{ source: pythonDefaultSource, id: 1, autoGenerated: true }],
      };
  }
}
export const defaultState: ExtendedStudioState = {
  mode: StudioMode.Config,
  rewrite: "logger.log($MATCH)",
  strictness: "smart",
  selector: "",
  currentSessionId: null,
  dirty: false,
  activeTestSourceId: 1,
  activeRemoteRunFileId: null,
  leftPanelCollapsed: false,
  leftPanelTab: "search",
  ruleErrors: undefined,
  ...getDefaultState("tsx"),
};
